// Shared client types mirroring the server API responses.

export interface FieldOption {
  value: string;
  text: string;
  /**
   * P3 image choice: a picture for this option on a 'radio' / 'multiselect'
   * field. When any option has one the renderer shows the choices as tiles.
   */
  image?: string;
  /**
   * SCORING: the points this answer earns. A question is a scored question as
   * soon as ONE of its options carries a number — the rest score nothing,
   * which is what "No" is: an answer that was asked and earned zero, not an
   * answer outside the scorecard. Blank and 0 therefore mean the same thing
   * for the earner, and the difference that matters lives on `exempt`.
   */
  score?: number;
  /**
   * SCORING: this answer takes the whole question OUT of the scorecard — the
   * N/A a mystery diner ticks when the dish was off the menu. It earns
   * nothing AND offers nothing, so a visit that could not be judged on a
   * question is not judged down for it either.
   */
  exempt?: boolean;
}

/** 'scale' fields: the run of numbers offered, and the words at its ends. Mirrors FieldScaleMeta on the server. */
export interface FieldScaleMeta {
  /** the first number — 0 (an NPS) or 1 */
  min: 0 | 1;
  /** the last number, 2..10 */
  max: number;
  minLabel?: string;
  maxLabel?: string;
}

/** 'matrix' fields: the questions down the side and the answers across the top. Mirrors FieldMatrixMeta on the server. */
export interface FieldMatrixMeta {
  rows: FieldOption[];
  columns: FieldOption[];
  /** each row takes a list of columns (checkboxes) rather than one (radios) */
  multi?: boolean;
}

export interface FieldUiMeta {
  showFlag?: string;
  requireFlag?: string;
  optionsKey?: string;
  defaultKey?: string;
  alwaysShow?: boolean;
  /** Shown by default (when no explicit showFlag value), subject to hideWith. */
  defaultShow?: boolean;
  alwaysRequired?: boolean;
  /** Required by default whenever visible, unless requireFlag is explicitly false (legacy `requireX !== false`). */
  requiredWhenShown?: boolean;
  hideWith?: string;
  placeholder?: string;
  infoText?: string;
  /** logical grouping/sub-card ("card") within a section */
  group?: string;
  /** fields sharing this non-empty label (and adjacent in sort order) render side by side on one row */
  row?: string;
  /** show this field only when another field's current value matches; orFlag = config flag that also reveals it */
  showWhen?: { field: string; equals: string; orFlag?: string };
  /** built-in option lists: 'us_states' (uses countryField) or 'countries' */
  optionsPreset?: 'us_states' | 'countries';
  /** field key holding the country that drives an optionsPreset:'us_states' list */
  countryField?: string;
  /** 'rating' fields: highest value on the scale (default 5, max 10) */
  ratingMax?: number;
  /** 'address' fields: composite behavior (labels/sub-fields follow the country);
   *  kind 'shipping' adds Company/Name, Attention and Phone Number sub-fields */
  address?: { defaultCountry?: string; kind?: 'standard' | 'shipping' };
  /** 'divider' fields: how the rule is drawn, and how much air it gets */
  divider?: { thickness?: number; color?: string; space?: number };
  /** 'heading' blocks: the text is the field's label; this is its size. */
  heading?: { level: 1 | 2 | 3 };
  /** 'paragraph' blocks: rich text in the tiny **bold** / *italic* / [link](https://…) subset. */
  paragraph?: { text: string };
  /** 'image' blocks: a /uploads URL from the assets route, or any https URL. */
  image?: { url: string; alt?: string; width?: number; align?: 'left' | 'center' | 'right' };
  /** 'signature' fields: how it may be signed, and how the pad looks.
   *  Mirrors FieldUiMeta.signature on the server. */
  signature?: {
    /** Ways to sign. Empty or absent = draw only. */
    modes?: ('draw' | 'type')[];
    /** Ink colour on the pad, and the colour a typed name is drawn in. */
    penColor?: string;
    /** Pad height in CSS pixels (default 160). */
    height?: number;
    /** Wording above the pad, e.g. "I agree to the terms above." */
    consentText?: string;
    /** The contract language this signature agrees to; {{tokens}} allowed. */
    terms?: string;
  };
  /**
   * The answer the form opens with — and that every new widget instance
   * starts from. A starting point, not a lock: the respondent can change it,
   * and a prefill rule still wins over it.
   */
  defaultValue?: string | number | boolean | string[];
  /**
   * Read-only on the form: shown, carrying whatever it starts from, and not
   * editable. The Build answer, which an `unlock` rule can still overrule —
   * the same shape as required, where a rule bends what Build says.
   */
  locked?: boolean;
  /** answer constraints, checked on the client and re-checked at submit */
  validation?: FieldValidation;
  /** 'payment' fields: what the field charges (amount resolved server-side) */
  payment?: {
    amountMode?: 'fixed' | 'field';
    /** dollars, mode 'fixed' */
    amountFixed?: number;
    /** key of the field/calc holding the dollar amount, mode 'field' */
    amountField?: string;
    currency?: string;
    description?: string;
  };
  /** 'file' fields: limits for this one field, over the form's `settings.uploads` */
  upload?: FieldUploadMeta;
  /**
   * 'radio' / 'select' / 'multiselect': offer a final "Other" choice with a
   * write-in. The typed text IS the answer (appended to the list for a
   * multiselect) — no sentinel is stored.
   */
  allowOther?: { label?: string };
  /** 'tel' fields: format as you type and keep the digits only */
  phone?: { format: 'us' };
  /** 'scale' fields (P3): the run of numbers and its end labels; absent = 1..5 */
  scale?: FieldScaleMeta;
  /** 'matrix' fields (P3): rows and columns; a field without them asks nothing */
  matrix?: FieldMatrixMeta;
}

/**
 * How a form marks what is required (`settings.requiredStyle`). Mirrors
 * FormRequiredStyle on the server. Absent = `{ marker: 'asterisk',
 * optionalSuffix: true }`, which is how every form read before U3.
 */
export interface FormRequiredStyle {
  /** an asterisk after the label, or the word "Required" */
  marker: 'asterisk' | 'label';
  /** "(optional)" after the fields that are not required */
  optionalSuffix: boolean;
}

/**
 * Per-field upload limits. Each key is optional and, when absent, the form's
 * `settings.uploads` (then the server default) answers instead. Mirrors
 * FieldUploadMeta on the server.
 */
export interface FieldUploadMeta {
  maxFiles?: number;
  maxSizeMb?: number;
  /** allowed extensions (lowercase, no dot); empty = inherit */
  accept?: string[];
}

/** A form's upload settings (`settings.uploads`). Mirrors FormUploadSettings on the server. */
export interface FormUploadSettings {
  enabled?: boolean;
  maxFiles?: number;
  maxSizeMb?: number;
  /** allowed extensions (lowercase, no dot); empty = the server's default allowlist */
  accept?: string[];
}

/** Per-field answer constraints. Only the keys that make sense for the field's
 *  input type are honored; everything is optional. */
export interface FieldValidation {
  /** minimum characters (text-like inputs) */
  minLen?: number;
  /** maximum characters (text-like inputs) */
  maxLen?: number;
  /** minimum numeric value ('number' fields) */
  min?: number;
  /** maximum numeric value ('number' fields) */
  max?: number;
  /** regular expression the whole answer must match (text-like inputs) */
  pattern?: string;
  /**
   * The window a 'date' answer has to fall in. Each bound is either a fixed
   * 'YYYY-MM-DD', or relative to the day the form is filled in: 'today',
   * 'today+10', 'today-30'. Relative bounds are resolved when the answer is
   * checked, so "no earlier than today" stays true tomorrow.
   */
  dateMin?: string;
  dateMax?: string;
  /** shown instead of the generic constraint message */
  message?: string;
}

/** How a date bound reads on screen, e.g. "today", "10 days from today". */
export function dateBoundText(spec: string): string {
  const rel = /^today(?:\s*([+-])\s*(\d{1,5}))?$/i.exec(spec.trim());
  if (!rel) return spec.trim();
  if (!rel[1]) return 'today';
  const days = Number(rel[2]);
  const unit = days === 1 ? 'day' : 'days';
  return rel[1] === '+' ? `${days} ${unit} from today` : `${days} ${unit} ago`;
}

/**
 * A date bound as a concrete 'YYYY-MM-DD', or null when it says nothing.
 *
 * Mirrored by server/src/lib/dateBounds.ts — the browser blocks the answer as
 * it is typed and the server refuses it again on the way in, so the two have
 * to resolve 'today + 10' to the same day.
 */
export function resolveDateBound(spec: string | undefined, today: Date = new Date()): string | null {
  const raw = (spec || '').trim();
  if (!raw) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const rel = /^today(?:\s*([+-])\s*(\d{1,5}))?$/i.exec(raw);
  if (!rel) return null;
  // Local midnight, then whole days: the answer is a calendar date, and a
  // timezone offset must not push "today" onto yesterday.
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (rel[1]) d.setDate(d.getDate() + (rel[1] === '-' ? -1 : 1) * Number(rel[2]));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// ---- Global form layout (Section -> Card -> Field) ----
export type SectionType = 'fields' | 'locations' | 'totals' | 'files' | 'widget';
/**
 * How a two-colour gradient runs. Mirrors the server's `GradientDir`.
 *
 * 'diagonal' is the 135deg every stock preset and the brand's own defaults
 * have always used, and 'radial' runs from the middle outwards — the "glow
 * behind the card" look, which is a gradient people ask for by describing it
 * rather than by naming it.
 */
export type GradientDir = 'vertical' | 'horizontal' | 'diagonal' | 'radial';

/** The four, in the order every picker offers them. */
export const GRADIENT_DIRS: readonly GradientDir[] = ['vertical', 'horizontal', 'diagonal', 'radial'];

/** What each one is called, and the arrow that shows it, on a button. */
export const GRADIENT_LABEL: Record<GradientDir, string> = {
  vertical: '\u2193 Vertical',
  horizontal: '\u2192 Horizontal',
  diagonal: '\u2198 Diagonal',
  radial: '\u25c9 From the centre',
};

/**
 * Two stops and a direction, as a CSS `background` value — the ONE place a
 * gradient is spelled.
 *
 * Every surface that can carry one goes through here: a section or card's
 * `bg`/`bg2`, the account brand's background and accent, and a form's page
 * background. Before this there were four copies of the same template string
 * with different angles baked into them, and the builder's copy had drifted
 * from the one a respondent got.
 *
 * `undefined` means 'diagonal', because that is the angle every stored
 * gradient was written at before a direction could be chosen — so a value
 * saved yesterday renders today at exactly the same angle.
 */
export function gradientCss(c1: string, c2: string, dir?: GradientDir): string {
  if (dir === 'radial') return `radial-gradient(ellipse at 50% 50%, ${c1} 0%, ${c2} 100%)`;
  const angle = dir === 'vertical' ? 'to bottom' : dir === 'horizontal' ? 'to right' : '135deg';
  return `linear-gradient(${angle}, ${c1} 0%, ${c2} 100%)`;
}

/**
 * The inverse, for the controls that hold a stored CSS string rather than a
 * pair of fields — the brand's background and a form's page background.
 *
 * Only the shapes `gradientCss` writes come back; anything else (a
 * hand-typed `conic-gradient`, a three-stop value from before there were
 * pickers) returns null so the screen can say the value is not one it wrote
 * instead of silently rounding it to something it can draw.
 */
export function parseGradient(
  value: string | undefined
): { c1: string; c2: string; dir: GradientDir } | null {
  const raw = (value || '').trim();
  const hexes = (raw.match(/#[0-9a-f]{6}/gi) || []).map((h) => h.toLowerCase());
  if (hexes.length !== 2) return null;
  const dir: GradientDir | null =
    /^radial-gradient\(\s*ellipse\s+at\s+50%\s+50%\s*,/i.test(raw) ? 'radial'
      : /^linear-gradient\(\s*to\s+bottom\s*,/i.test(raw) ? 'vertical'
        : /^linear-gradient\(\s*to\s+right\s*,/i.test(raw) ? 'horizontal'
          : /^linear-gradient\(\s*135deg\s*,/i.test(raw) ? 'diagonal'
            : null;
  if (!dir) return null;
  return { c1: hexes[0], c2: hexes[1], dir };
}

/** Presentation styling for a section or a card (set in the Form Builder). */
export interface SectionStyle {
  /** solid colour, or the FIRST stop when `bg2` is set */
  bg?: string;
  /** second gradient stop — its presence turns `bg` into a gradient */
  bg2?: string;
  /** gradient direction (only meaningful with bg2) */
  gradient?: GradientDir;
  radius?: number;
  width?: '100%' | '75%' | '50%';
  height?: string;
}
export interface FormCard {
  id: string;
  title: string;
  sortOrder?: number;
  style?: SectionStyle;
  /**
   * Hidden by default: it renders only while a `show` rule on the Logic page
   * names it and fires — the container-level twin of a field's Hidden setting.
   * Absent (or false) = on the form unless a rule takes it away.
   */
  hidden?: boolean;
  /**
   * Which container the card groups fields inside. Absent (or 'order') = the
   * section body, asked once per submission. A widget node's scope = the card
   * lives inside that repeatable widget and repeats with every instance of it.
   */
  scope?: string;
  /**
   * The card this one sits INSIDE, when it groups a sub-set of another card's
   * questions. Absent = the container's own body. A card can only nest inside
   * a card of the same section and scope, at most MAX_CARD_DEPTH deep.
   */
  parent?: string;
  /**
   * Where the card sits among the loose fields of its container, on the same
   * scale as a field's `sort_order`. Absent on layouts published before cards
   * could be interleaved — those still render after the container's fields.
   */
  order?: number;
}

/** A card, a card inside it, and a card inside that. Mirrored on the server. */
export const MAX_CARD_DEPTH = 3;
export interface FormSection {
  id: string;
  title: string;
  /** rendered under the title, in the same rich-text subset a paragraph uses */
  desc?: string;
  type: SectionType;
  sortOrder?: number;
  special?: 'partnerHeader';
  showFlag?: string;
  hideWith?: string;
  cards?: FormCard[];
  /** type 'widget' only: which FormWidget the section renders. */
  widgetId?: string;
  style?: SectionStyle;
  /**
   * Hidden by default: it renders only while a `show` rule on the Logic page
   * names it and fires — the container-level twin of a field's Hidden setting.
   * Absent (or false) = on the form unless a rule takes it away.
   */
  hidden?: boolean;
  /** this section starts a new page of the form */
  pageBreak?: boolean;
  /** shown in the progress bar for the page this section starts */
  pageTitle?: string;
}
export interface FormLayout { version: number; sections: FormSection[] }

// ---- Logic rules (meta 'form_rules') ----
// A rule is ONE shape: a trigger plus a list of actions. The trigger is either
// a condition group ('when') or 'always'; every action it carries then runs off
// it. There is no second rule kind — a calculation is an action like any other,
// which is what lets one trigger show a card, make a field required and set a
// total at the same time.
/**
 * A condition's operator. `is`/`isnot`/`includes`/`notincludes` compare as
 * text; `gt`/`lt`/`gte`/`lte` compare as numbers and never fire when either
 * side is not one; `before`/`after` compare ISO dates or date-times (to the
 * day when only one side carries a time); `contains` is a case-insensitive
 * substring on text and "any element equals" on a list. The contract is
 * shared/conditionCases.json, which both evaluators are tested against.
 */
export type RuleOp =
  | 'is' | 'isnot' | 'includes' | 'notincludes' | 'filled' | 'empty'
  | 'gt' | 'lt' | 'gte' | 'lte' | 'before' | 'after' | 'contains';
export type RuleWhen = { field: string; op: RuleOp; value?: string };

/**
 * One row of a prefill action: what gets written into `to`.
 *
 * `from` names another answer and the row mirrors it. `value` is a literal the
 * rule carries instead — the fixed answer to write — which is how a dropdown
 * gets one of its own options put into it while STAYING the dropdown the
 * respondent sees, editable or locked as the action's mode says. Exactly one
 * of the two is set, and `value` wins if somehow both are: a literal cannot go
 * stale the way a source field deleted from the form can.
 */
export type PrefillMap = { to: string; from?: string; value?: string };

export type RuleAction =
  | { type: 'show'; targets: string[] }
  | { type: 'hide'; targets: string[] }
  /** required WHILE the rule holds — a field the rule does not reach keeps what Build says */
  | { type: 'require'; targets: string[] }
  /** optional while the rule holds; beats `require` for the same reason hide beats show */
  | { type: 'optional'; targets: string[] }
  /** read-only while the rule holds — the answer stays, it is just not theirs to change */
  | { type: 'lock'; targets: string[] }
  /** editable while the rule holds; beats `lock`, and beats Build's own Locked answer */
  | { type: 'unlock'; targets: string[] }
  | { type: 'prefill'; mode: 'editable' | 'locked'; map: PrefillMap[] }
  /** the old `kind: 'calc'` rule, now one action among many */
  | { type: 'calc'; target: string; calc: CalcSpec }
  /**
   * Skip ahead to the page that `page` (a section id that starts one) opens.
   * Only the pager reads it, on Next, and only forward: the pages jumped over
   * are neither validated nor submitted.
   */
  | { type: 'goto'; page: string };

export type CalcSpec =
  | { type: 'count'; of: string }
  | { type: 'sum'; field: string; of: string }
  | { type: 'math'; a: string; op: '+' | '-' | '*'; b: string }
  | { type: 'join'; fields: string[]; sep: string }
  | { type: 'fixed'; value: string }
  /**
   * A scorecard total, read off the points on the ANSWER OPTIONS rather than
   * off a list of fields — so a question added to the section later joins the
   * total on its own, and a question renamed never leaves a stale row behind.
   *
   * 'earned' totals the points the answers actually won; 'available' totals
   * the points those same questions had to give; 'percent' is the first over
   * the second. `section` narrows all three to one section of the form, which
   * is how a scorecard gets a subtotal per area; left off, it is the whole
   * form. A question nobody has answered yet, one a rule is hiding, and one
   * answered with an `exempt` option are all outside BOTH totals.
   */
  | { type: 'score'; mode: 'earned' | 'available' | 'percent'; section?: string };

export type FormRule = {
  id: string;
  kind: 'logic';
  name: string;
  /** 'when' waits for the condition group; 'always' runs on every render */
  trigger: 'when' | 'always';
  match: 'all' | 'any';
  when: RuleWhen[];
  actions: RuleAction[];
};

/** Stored rule shapes that are still READ but are no longer authored. */
export type LegacyRule =
  | { id: string; kind: 'visibility'; name: string; when: RuleWhen; action: 'show' | 'hide'; targets: string[]; orFlag?: string }
  | { id: string; kind: 'prefill'; name: string; when: RuleWhen; mode: 'editable' | 'locked'; map: { from: string; to: string }[] }
  | { id: string; kind: 'calc'; name: string; target: string; calc: CalcSpec };

/**
 * Upgrade a stored rule to the one-rule model. A load-time upgrade, not a
 * migration: nothing is rewritten in the database until the form is saved.
 *
 * The `trigger` a stored rule gets is the whole care of this function:
 *
 *  - a `kind: 'calc'` rule becomes `trigger: 'always'` with one `calc` action,
 *    which is exactly what it did before — calculations never had a "when".
 *  - EVERY stored `kind: 'logic'` rule becomes `trigger: 'when'`, including one
 *    whose condition list is empty. Such a rule has never fired (an empty
 *    condition group never holds), so reading it as 'always' would start it
 *    firing on a live form the moment this code shipped. The Logic page now
 *    says out loud that it never runs, and offers the switch to 'always'.
 */
export function normalizeRule(raw: FormRule | LegacyRule): FormRule {
  const r = raw as any;
  const id = String(r.id);
  const name = String(r.name ?? '');
  if (r.kind === 'calc') {
    return {
      id, kind: 'logic', name, trigger: 'always', match: 'all', when: [],
      actions: [{ type: 'calc', target: String(r.target || ''), calc: r.calc }],
    };
  }
  if (r.kind === 'logic') {
    return {
      id, kind: 'logic', name,
      trigger: r.trigger === 'always' ? 'always' : 'when',
      match: r.match === 'any' ? 'any' : 'all',
      when: Array.isArray(r.when) ? r.when : r.when ? [r.when] : [],
      actions: Array.isArray(r.actions) ? r.actions : [],
    };
  }
  const when: RuleWhen[] = r.when ? [r.when] : [];
  if (r.kind === 'visibility') {
    return {
      id, kind: 'logic', name, trigger: 'when', match: 'all', when,
      actions: [{ type: r.action === 'hide' ? 'hide' : 'show', targets: r.targets || [] }],
    };
  }
  return {
    id, kind: 'logic', name, trigger: 'when', match: 'all', when,
    actions: [{ type: 'prefill', mode: r.mode === 'locked' ? 'locked' : 'editable', map: r.map || [] }],
  };
}

/**
 * Every rule, upgraded to the current shape and guaranteed a UNIQUE id.
 *
 * A rule is edited and deleted by id, so two rules sharing one made every
 * edit land on both and every delete take both. Stored definitions written
 * before ids were minted safely still carry collisions, so they are broken
 * apart here — the one place every load already passes through.
 */
export const normalizeRules = (list: (FormRule | LegacyRule)[] | undefined): FormRule[] => {
  const seen = new Set<string>();
  return (list || []).map(normalizeRule).map((r) => {
    if (r.id && !seen.has(r.id)) { seen.add(r.id); return r; }
    const base = r.id || 'r';
    let n = 2;
    while (seen.has(`${base}_${n}`)) n += 1;
    const id = `${base}_${n}`;
    seen.add(id);
    return { ...r, id };
  });
};

// ---- Widgets (meta 'form_widgets') — repeatable containers as a TREE --------
// Any widget can embed child widgets; each node repeats independently within
// its parent instance (its add button clones just that node + its children).

/**
 * A count of only SOME of a container's instances: the ones a condition group
 * holds for ("devices whose type is not Bring my own"). It travels exactly
 * the way the automatic count does — every instance above reports its own
 * share under `key`, and the submission carries the form-wide total — so an
 * integration maps it like any other number.
 *
 * The group is the Logic page's own (`match` + `when`), read against one
 * instance's answers laid over the answers above it. An EMPTY group holds for
 * nothing, the same reading an unfinished rule gets, so the count is 0.
 */
export interface WidgetCount {
  key: string;
  label: string;
  match: 'all' | 'any';
  when: RuleWhen[];
}

export interface WidgetNode {
  scope: string;
  itemLabel: string;
  addLabel: string;
  min: number;
  max: number; // 0 = unlimited
  /** instance cards per row (1 = full width, up to 4 side by side) */
  perRow?: number;
  countKey: string;
  /** counts of the instances a condition holds for, beside the automatic one */
  counts?: WidgetCount[];
  summaryField?: string;
  /**
   * Where this container sits among its PARENT's own fields and cards, on the
   * same scale as a field's `sort_order`. Absent on widgets published before
   * a nested one could be placed — those still render after everything else
   * in the container that holds them.
   */
  order?: number;
  /**
   * Presentation for the instance cards this container repeats — the same
   * styling a section and a card carry, applied to every instance of it.
   */
  style?: SectionStyle;
  /**
   * Hidden by default: it renders only while a `show` rule on the Logic page
   * names it and fires — the container-level twin of a field's Hidden setting.
   * Absent (or false) = on the form unless a rule takes it away.
   */
  hidden?: boolean;
  children: WidgetNode[];
}
export interface FormWidget { id: string; name: string; desc?: string; builtin?: boolean; tree: WidgetNode }

/** Flattened pre-order view of a widget tree (depth + parent labels attached). */
export function flattenWidgetTree(
  node: WidgetNode, depth = 0, parent: WidgetNode | null = null,
  out: (WidgetNode & { depth: number; parentScope: string; parentLabel: string })[] = []
) {
  out.push({ ...node, depth, parentScope: parent ? parent.scope : '', parentLabel: parent ? parent.itemLabel : '' });
  (node.children || []).forEach((c) => flattenWidgetTree(c, depth + 1, node, out));
  return out;
}

export interface FieldDefinition {
  id: number;
  key: string;
  label: string;
  section: string;
  input_type:
    | 'text' | 'email' | 'tel' | 'date' | 'number' | 'select' | 'checkbox' | 'textarea' | 'file'
    | 'radio' | 'multiselect' | 'rating' | 'time' | 'url' | 'payment' | 'name' | 'address'
    /** a drawn or typed signature; the answer is the URL of the stored PNG */
    | 'signature'
    /** P3: a linear scale, rows × columns, options in order, a date with a time */
    | 'scale' | 'matrix' | 'ranking' | 'datetime'
    /** presentation only — a rule, a heading, a paragraph or a picture; none asks anything */
    | 'divider' | 'heading' | 'paragraph' | 'image';
  options: FieldOption[];
  flow_applicability: 'simple' | 'complex' | 'both';
  /** 'order' or a widget-node scope ('location', 'contact', 'device', 'line', …) */
  scope: string;
  sort_order: number;
  required_default: boolean;
  ui_meta: FieldUiMeta;
}

/**
 * Field types that ask nothing. They live in the field catalog because that is
 * where position lives, but they carry no answer: nothing to submit, nothing
 * to validate, nothing to map to an integration, and no column in the table.
 */
export const PRESENTATION_TYPES = ['divider', 'heading', 'paragraph', 'image'];
export const asksNothing = (inputType: string): boolean => PRESENTATION_TYPES.includes(inputType);

export type FormConfig = Record<string, any>;

export interface SubmitResult {
  success: boolean;
  submissionId?: string;
  mainItemId?: string;
  mondayLink?: string;
  error?: string;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Single sign-on
// ---------------------------------------------------------------------------

export type ProviderProtocol = 'oidc' | 'oidc_manual' | 'saml';
export type CheckStatus = 'pass' | 'fail' | 'warn' | 'pending';
export interface CheckResult { status: CheckStatus; detail: string }

/** What could be proved about a provider without anyone signing in. */
export interface ConnectionChecks {
  ranAt: string;
  /** endpoints discovery produced, kept so the rail survives a reload */
  resolved?: { issuer?: string; authorization_endpoint?: string; token_endpoint?: string; jwks_uri?: string };
  discovery: CheckResult;
  jwks: CheckResult;
  flow: CheckResult;
  credentials: CheckResult;
  redirectUri: CheckResult;
  liveSignIn: CheckResult;
}

/** Deliberately avoids "verified" and "never tested" — see the handoff. */
export type ProviderStatus = 'not_checked' | 'checks_passed' | 'live' | 'failing';

export interface ProviderUsage {
  teamSignIn: boolean;
  forms: { id: number; name: string; slug: string }[];
}

/** A provider as the browser sees it — the client secret is never included. */
export interface IdentityProvider {
  id: number;
  name: string;
  slug: string;
  protocol: ProviderProtocol;
  config: {
    discoveryUrl?: string;
    issuer?: string;
    authorizationEndpoint?: string;
    tokenEndpoint?: string;
    jwksUri?: string;
    clientId?: string;
    scopes?: string[];
    metadataXml?: string;
    metadataUrl?: string;
    entityId?: string;
    /** accept an assertion that answers no AuthnRequest (IdP-initiated) */
    allowIdpInitiated?: boolean;
    /** a secret is stored; its value never leaves the server */
    clientSecretSet?: boolean;
  };
  allowedDomains: string[];
  claimNames: string[];
  checks: ConnectionChecks | null;
  checkedAt: string | null;
  lastSignInAt: string | null;
  status: ProviderStatus;
  checksClear: boolean;
  callbackUri: string;
  dedicatedCallbackUri: string;
  usage?: ProviderUsage;
}

/** The customer's ladder since 044 — see server/src/lib/accountRoles.ts. */
export type SsoAccountRole = 'owner' | 'admin' | 'member';

/** How one account's team signs in to Form-Magic. */
export interface AccountSso {
  mode: 'password' | 'optional' | 'required';
  /** always providerIds[0]; kept for accounts saved before the list existed */
  providerId?: number;
  /** every provider allowed to sign the TEAM in — not the whole library */
  providerIds: number[];
  provisioning: 'domain' | 'invite' | 'domain_roles';
  allowedDomains: string[];
  defaultRole: SsoAccountRole;
  roleClaim?: string;
  roleMap?: Record<string, SsoAccountRole>;
}

export const PROVIDER_STATUS_LABEL: Record<ProviderStatus, string> = {
  not_checked: 'Not checked',
  checks_passed: 'Checks passed',
  live: 'Live sign-ins working',
  failing: 'Checks failing',
};

/** Pill colours, straight from the handoff's status table. */
export const PROVIDER_STATUS_STYLE: Record<ProviderStatus, { background: string; color: string }> = {
  not_checked: { background: '#fefcbf', color: '#744210' },
  checks_passed: { background: '#bee3f8', color: '#2c5282' },
  live: { background: '#c6f6d5', color: '#22543d' },
  failing: { background: '#fed7d7', color: '#742a2a' },
};

export const PROTOCOL_LABEL: Record<ProviderProtocol, string> = {
  oidc: 'OIDC · discovery',
  oidc_manual: 'OIDC · endpoints by hand',
  saml: 'SAML 2.0 · metadata',
};

/** One extra condition a signed-in respondent must satisfy for a form. */
export interface ClaimRequirement { claim: string; op: 'is' | 'isnot' | 'contains'; value: string }

/** forms.settings.access — who may open and submit ONE published form. */
export interface FormAccess {
  mode: 'open' | 'password' | 'sso' | 'magic_link' | 'invite';
  providerId?: number;
  buttonLabel?: string;
  allowedDomains?: string[];
  requirements?: ClaimRequirement[];
  stamp?: boolean;
  oncePerPerson?: boolean;
  claimMap?: { claim: string; field: string }[];
  magicLink?: { ttlMinutes: number };
  /**
   * U6 — the key of an email field: one submission per distinct answer,
   * compared case-insensitively. Works in every mode; it is the only
   * once-per-person an open form can have.
   */
  oncePerEmail?: string;
  /**
   * U6 — soft: a browser that has submitted is shown the duplicate message
   * on return (a localStorage marker, nothing the server enforces).
   */
  oncePerBrowser?: boolean;
  /** U6 — what a repeat respondent sees; blank means the default sentence. */
  duplicateMessage?: string;
}

/**
 * Bot protection (U6). `captcha` asks every anonymous respondent (access
 * mode open or password) to pass Cloudflare Turnstile before submitting.
 * Stored on every form; obeyed only while the platform has Turnstile keys.
 */
export interface FormSpamSettings {
  captcha?: boolean;
}

/**
 * URL prefill (P1): one query parameter the public link may carry, and the
 * order-scope field (or composite part, `addr.city`) it fills. `lock` is
 * honoured only on a signed link — an unsigned value is whoever holds the
 * URL's to type, so it is an editable default and no more.
 */
export interface PrefillParam {
  param: string;
  field: string;
  lock?: boolean;
}

/** forms.settings.prefill — absent means the feature is off for the form. */
export interface FormPrefillSettings {
  params: PrefillParam[];
  /** Bumped by "Rotate signing key"; every signed link issued before stops verifying. Absent reads as 1. */
  keyVersion?: number;
}

/** The verified identity recorded against a submission. */
export interface Submitter {
  kind: 'sso' | 'magic_link' | 'password' | 'anonymous' | 'invite';
  /** Invite submissions only: who actually filled the form in. */
  filledBy?: 'rep' | 'customer';
  inviteId?: number;
  providerId?: number;
  providerName?: string;
  sub?: string;
  issuer?: string;
  email?: string;
  name?: string;
  claims?: Record<string, unknown>;
  signedInAt?: string;
  verifiedAt?: string;
}

export const ACCESS_MODE_LABEL: Record<FormAccess['mode'], string> = {
  open: 'Open — anyone with the link',
  password: 'Password',
  sso: 'SSO',
  magic_link: 'Email magic link',
  invite: 'Invite link only — continues another form',
};

/**
 * This form continues another one (the order handoff). Mirrors server/src/types/form.ts.
 *
 * Set on the SECOND form — the one an invite opens. It says which forms this
 * one may continue and which of their answers are carried across, each with
 * how the person filling this one in may treat it:
 *
 *   locked    shown, not editable. Re-applied server-side at submit, so a
 *             crafted request cannot change it either.
 *   confirm   shown, editable, and they must say whether it is right. What is
 *             recorded is the ANSWER AND THE VERDICT — "the customer confirmed
 *             the billing contact" is a different fact from "the billing
 *             contact field contains this string", and only one of them is
 *             worth anything when a contract is disputed.
 *   editable  filled in as a starting point, with nothing asked about it.
 *
 * One child can continue many parents — one "Location details" form behind
 * every partner's order form. The invite link is the credential; the list is
 * a guardrail. `parentFormId` is the single-parent spelling every row written
 * before 2026-09-17 has; `continuationParents` reads both.
 */
export interface FormContinuation {
  parentFormIds?: number[];
  /** whichever form on the account minted the link that opened it */
  anyParent?: boolean;
  /** @deprecated the pre-2026-09-17 spelling — read, never written */
  parentFormId?: number;
  map: { from: string; to: string; mode: 'locked' | 'confirm' | 'editable' }[];
  confirmPrompt?: string;
}

/** The parents a continuation names, whichever spelling the row has. */
export function continuationParents(c: FormContinuation | null | undefined): { anyParent: boolean; ids: number[] } {
  if (!c) return { anyParent: false, ids: [] };
  const ids = [...new Set([...(c.parentFormIds || []), ...(c.parentFormId ? [c.parentFormId] : [])])]
    .filter((id) => Number.isInteger(id) && id > 0);
  return { anyParent: !!c.anyParent, ids };
}

/** Does this continuation continue `formId`? The listed ids, or anything at all. */
export function continuesForm(c: FormContinuation | null | undefined, formId: number): boolean {
  const p = continuationParents(c);
  return p.anyParent || p.ids.includes(formId);
}

/** Is this a continuation at all — does it name a parent, or accept any? */
export const isContinuation = (c: FormContinuation | null | undefined): boolean => {
  const p = continuationParents(c);
  return p.anyParent || p.ids.length > 0;
};

/** What a carried-over answer looks like on the form the person is filling in. */
export interface CarriedField {
  /** the value as the parent submission had it */
  from: unknown;
  mode: 'locked' | 'confirm' | 'editable';
}

/** Reserved answer key holding the carried-over record (the order handoff). */
export const CARRIED_KEY = '_carried';


// ---------------------------------------------------------------------------
// Submissions table
// ---------------------------------------------------------------------------

export type FilterOp =
  | 'is' | 'isnot' | 'contains' | 'notcontains' | 'empty' | 'notempty'
  | 'gt' | 'lt' | 'gte' | 'lte' | 'before' | 'after';

export interface FilterCondition { column: string; op: FilterOp; value?: string | number }

export type DeliveryFilter = 'any' | 'any_failed' | 'pending' | 'all_delivered' | 'none_sent';

export interface SubmissionFilters {
  q?: string;
  delivery?: DeliveryFilter;
  connectionId?: number;
  from?: string;
  to?: string;
  match?: 'all' | 'any';
  conditions?: FilterCondition[];
}

/** A saved view stores filters, column set, column order and sort together. */
export interface ViewConfig {
  columns: string[];
  sort: { column: string; dir: 'asc' | 'desc' };
  filters: SubmissionFilters;
}

/** Every delivery for one submission, reduced to what a table row has space for. */
export interface DeliverySummary {
  total: number;
  delivered: number;
  failed: number;
  pending: number;
  skipped: number;
  state: 'all_delivered' | 'any_failed' | 'pending' | 'partial' | 'none_sent';
  firstError?: { connectionType: string; connectionName: string; message: string };
  lastResendAt?: string;
  lastResendBy?: string;
}

/**
 * What someone may do when RUNNING the service (see the server's
 * lib/platformRoles.ts, which is where the capability rules actually live —
 * the client only uses this to decide what to draw).
 */
export type PlatformRole = 'staff' | 'admin' | 'superadmin';

export const PLATFORM_ROLE_LABEL: Record<PlatformRole, string> = {
  staff: 'Staff',
  admin: 'Admin',
  superadmin: 'Super Admin',
};

const PLATFORM_RANK: Record<PlatformRole, number> = { staff: 1, admin: 2, superadmin: 3 };

/** Is `role` at least `min`? Mirrors the server's monotonic ladder. */
export const atLeastPlatform = (role: PlatformRole | null | undefined, min: PlatformRole): boolean =>
  !!role && PLATFORM_RANK[role] >= PLATFORM_RANK[min];

// ---------------------------------------------------------------------------
// Plans
//
// The catalogue the platform console edits. Mirrors server/src/lib/plans.ts —
// the two files are the same shape on purpose, the way PlatformRole already is.
// ---------------------------------------------------------------------------

/** A ceiling per resource. `null` is UNLIMITED, and means the check never runs. */
export interface PlanLimits {
  forms: number | null;
  submissionsPerMonth: number | null;
  users: number | null;
  storageBytes: number | null;
  submissionRecords: number | null;
  /** Every element on a form: each field AND each repeatable widget container. */
  fieldsPerForm: number | null;
  /** Successful AI form generations per calendar month; each spends the
   *  platform's own provider tokens, so use is a counted ceiling (065). */
  aiGenerationsPerMonth: number | null;
  /** Questions to the form assistant ("Ask AI" inside a form) per calendar
   *  month (068) — its own counted ceiling under the same aiBuilder feature. */
  aiAssistantMessagesPerMonth: number | null;
}

/**
 * The server's `PlanFeatures`, key for key.
 *
 * Every key MUST be here. The plan editor posts the whole object and the
 * server reads absent keys as false, so a feature this interface has not
 * heard of is a feature that silently switches itself off the next time
 * somebody edits a plan — which is exactly what happened to 044's team
 * access keys, missing here from 044 until 047.
 */
export interface PlanFeatures {
  sso: boolean;
  /** Bearer-key access to the public REST API. */
  api: boolean;
  /** Logo, fonts, presets, dark mode on forms. */
  customBranding: boolean;
  /**
   * Working as a team (044, one key since 048): folders on the forms list,
   * the role ladder with its grants, and named groups. One feature, because
   * folders you cannot restrict are just labels and a group exists to be
   * handed a grant — they do not work apart, so they are not sold apart.
   */
  teamAccess: boolean;
  /**
   * Smart PDF: upload a template and have answers stamped onto it, so what a
   * customer signs is their own paperwork rather than our record of it. The
   * branded record stays free — what is sold is the template.
   */
  smartPdf: boolean;
  /** Turn the Form-Magic badge on a published form off, per form. */
  hideAdvertisement: boolean;
  /**
   * The AI form builder (065): describe a form, get a draft. Never on Free —
   * every generation spends the platform's provider tokens — and metered by
   * `aiGenerationsPerMonth` beside it. Also the form assistant's access (068),
   * which has its own `aiAssistantMessagesPerMonth` ceiling.
   */
  aiBuilder: boolean;
  /** Account webhooks (086): one endpoint told about everything that happens to the account's submissions. */
  webhooks: boolean;
}

/** Editor and matrix order; also the order the seeded plans read in. */
export const LIMIT_KEYS: (keyof PlanLimits)[] = [
  'forms', 'submissionsPerMonth', 'users', 'storageBytes',
  'submissionRecords', 'fieldsPerForm', 'aiGenerationsPerMonth', 'aiAssistantMessagesPerMonth',
];

/**
 * Editor order, and the list the editor POSTS. Mirrors the server's
 * FEATURE_KEYS: reading the checkboxes off this rather than by hand is what
 * stops a new feature from being dropped on the next plan save.
 */
export const FEATURE_KEYS: (keyof PlanFeatures)[] = [
  'sso', 'api', 'customBranding', 'teamAccess', 'hideAdvertisement', 'smartPdf', 'aiBuilder', 'webhooks',
];

export interface Plan {
  id: number;
  key: string;
  name: string;
  tagline: string;
  sort_order: number;
  public: boolean;
  popular: boolean;
  trialable: boolean;
  retired_at: string | null;
  price_monthly_cents: number | null;
  annual_discount_pct: number;
  /** derived on the server from the monthly price and the discount */
  price_annual_cents: number | null;
  currency: string;
  limits: PlanLimits;
  features: PlanFeatures;
  /** all null until someone pushes the plan to Stripe, which needs credentials */
  stripe_product_id: string | null;
  stripe_price_monthly: string | null;
  stripe_price_annual: string | null;
  stripe_synced_at: string | null;
  /**
   * The amounts the LIVE Stripe Prices carry, written back at push time.
   * A saved price that differs from these is a draft — visible in 1b/1c as
   * "not pushed" — because editing a price never edits a Stripe Price.
   */
  pushed_monthly_cents?: number | null;
  pushed_annual_cents?: number | null;
  created_at: string;
  updated_at: string;
}

/** A plan plus what depends on it, as the Plans table lists it. */
export interface PlanSummary extends Plan {
  account_count: number;
  archived_price_count: number;
}

// ---------------------------------------------------------------------------
// Billing — the Stripe mirror, as the platform screens read it.
//
// `subscription_status` is a mirror written only by the webhook handler; the
// client renders it verbatim and never infers one.
// ---------------------------------------------------------------------------

export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete';

/** What the console's account rows carry beyond the account itself. */
export interface ConsoleBillingFields {
  billing_interval: 'month' | 'year';
  subscription_status: SubscriptionStatus | null;
  current_period_end: string | null;
  /** the active free upgrade period, naming the trialled plan — not the paid one */
  trial: { plan_key: string; plan_name: string; days_left: number } | null;
  /** normalised to a month for annual subscribers, after the agreed price and a running percent-off; trials contribute nothing */
  mrr_cents: number;
  failed_attempts: number;
  /** the single closest-to-its-ceiling metric; null on an unlimited plan. `pct` is a whole percent, 0–100 */
  top_usage: { limit: keyof PlanLimits; used: number; allowed: number | null; pct: number } | null;
  /** R12: Stripe says past_due, or the account's own status does — the Past due tile counts these */
  past_due?: boolean;
  /**
   * Ben, 2026-09-27: the end of a declined card's 15-day grace — the day the
   * account moves to Free if it still has not paid (jobs/paidPlans.ts).
   */
  grace_until?: string | null;
}

export type TrialStatus = 'active' | 'converted' | 'ended' | 'revoked';

/** One free upgrade period, active or historical. */
export interface AccountTrial {
  id: number;
  plan_id: number;
  plan_key: string;
  plan_name: string;
  starts_at: string;
  /** end of day in the account's timezone */
  expires_at: string;
  card_required: boolean;
  auto_convert: boolean;
  notify: boolean;
  /** internal, never shown to the tenant */
  note: string | null;
  granted_by_name: string;
  status: TrialStatus;
  resolved_at?: string | null;
  resolution_note?: string | null;
  /** the plan a revoke returns them to; null on periods granted before 054 */
  previous_plan_key?: string | null;
  previous_plan_name?: string | null;
  /** the restore point's plan id, and the end of the period they had already paid for (054) */
  previous_plan_id?: number | null;
  paid_through?: string | null;
}

/** One row of the read-only reconcile report behind `Sync from Stripe`. */
/** `ok: 'unknown'` = nothing to judge yet (a webhook that has never fired). */
export interface PlanSyncCheck { name: string; ok: boolean | 'unknown'; detail: string }
export interface PlanSyncReport { plans: { plan_key: string; checks: PlanSyncCheck[] }[] }

// ---------------------------------------------------------------------------
// Tenant billing — the picker, the downgrade gate and the banners.
//
// Shapes mirror server/src/lib/billing.ts and the /admin/billing/banner
// route; the client renders these, it never derives billing state itself.
// ---------------------------------------------------------------------------

/** A plan as `GET /api/plans` hands it to the picker: catalogue fields only. */
export interface TenantPlan {
  id: number;
  key: string;
  name: string;
  tagline: string;
  sort_order: number;
  popular: boolean;
  retired: boolean;
  price_monthly_cents: number | null;
  price_annual_cents: number | null;
  annual_discount_pct: number;
  currency: string;
  limits: PlanLimits;
  features: PlanFeatures;
  current: boolean;
}

/** One limit the target plan cannot hold, and the way out of it. */
export interface DowngradeCheckItem {
  limit: 'storageBytes' | 'forms' | 'users' | 'submissionRecords' | 'sso' | 'customDomains';
  used: number;
  allowed: number | null;
  /** used <= allowed */
  resolved: boolean;
  /** must be resolved before the change */
  blocking: boolean;
  action: 'delete_files' | 'disable_forms' | 'remove_users' | 'none';
  /** the sentence rendered in the row, written server-side */
  detail: string;
}

/** The gate. All six rows are always present, resolved or not. */
export interface DowngradeCheck {
  targetPlan: string;
  /** true if any blocking item is unresolved */
  blocked: boolean;
  items: DowngradeCheckItem[];
}

/** The live free period, as the banner endpoint states it. */
export interface BannerTrial {
  planKey: string;
  planName: string;
  expiresAt: string;
  autoConvert: boolean;
  convertAmountCents: number | null;
  interval: 'month' | 'year';
  /** a period that does not convert: the plan it goes back to, named and priced (D3) */
  previousPlanName?: string;
  previousAmountCents?: number | null;
  /** it does not convert and Stripe holds the subscription: it ends, and the account moves to Free */
  endsSubscription?: boolean;
}

/** A period that just resolved — converted, quietly ended, or revoked. */
export interface BannerRecentTrial {
  status: 'converted' | 'ended' | 'revoked';
  planKey: string;
  planName: string;
  resolvedAt: string;
  paidPlanKey: string;
  paidPlanName: string;
  readOnlyForms: { id: number; name: string }[];
}

/** Dunning as Stripe reports it; the client renders the stage, never times it. */
export interface BannerDunning {
  status: 'past_due' | 'moved_to_free';
  amountCents: number;
  attemptCount: number;
  firstFailedAt: string | null;
  finalAttemptAt: string | null;
  graceUntil: string | null;
  restorePlanKey: string;
  restorePlanName: string;
  restoreAmountCents: number | null;
  keptForms: number;
  keptSubmissions: number;
  readOnlyForms: { id: number; name: string }[];
}

/** What `GET /api/admin/billing/banner` answers, for any account admin. */
export interface BillingBanner {
  trial: BannerTrial | null;
  recentTrial: BannerRecentTrial | null;
  dunning: BannerDunning | null;
}

/** One form's uploads, as the Billing storage section lists them. */
export interface StorageForm { id: number; name: string; bytes: number; disabled: boolean }

// ---- branding: the logo row --------------------------------------------

/** Where a logo sits on the header row. One logo per position, at most. */
export type LogoAlign = 'left' | 'center' | 'right';

/** The three positions, in the order they are drawn. */
export const LOGO_ALIGNS: readonly LogoAlign[] = ['left', 'center', 'right'];

/** An uploaded logo. `url` is one of our /uploads URLs. */
export interface FormLogo { url: string; height?: number; align: LogoAlign }

/**
 * A theme's logos, in drawing order — the ONE way the client reads them.
 *
 * The server's `themeLogos` in reverse: a form saved before the row could hold
 * three carries a single `logo`, and it must keep rendering until its next
 * save rewrites it as `logos`. Positions are unique — a second logo claiming a
 * taken one is dropped — and the order is left, centre, right.
 */
export function themeLogos(
  theme: { logos?: FormLogo[]; logo?: { url: string; height?: number; align?: LogoAlign } } | undefined
): FormLogo[] {
  const list = theme?.logos?.length
    ? theme.logos
    : (theme?.logo?.url ? [{ ...theme.logo, align: theme.logo.align || 'center' }] : []);
  const seen = new Set<LogoAlign>();
  return list
    .filter((l): l is FormLogo => !!l && typeof l.url === 'string' && !!l.url)
    .map((l) => ({ ...l, align: LOGO_ALIGNS.includes(l.align) ? l.align : 'center' }))
    .filter((l) => (seen.has(l.align) ? false : (seen.add(l.align), true)))
    .sort((a, b) => LOGO_ALIGNS.indexOf(a.align) - LOGO_ALIGNS.indexOf(b.align));
}

// ---- branding: the account's brand ----------------------------------------

/** One colour an account has a NAME for. Mirrors the server's `BrandSwatch`. */
export interface BrandSwatch {
  name: string;
  /** '#rrggbb' */
  hex: string;
  /** The sub-brand it belongs to, for companies that have them. */
  group?: string;
}

/**
 * `accounts.settings.brand` — ONE account's house style. Mirrors the server's
 * `AccountBrand`; every field is a DEFAULT a form inherits until it answers
 * the same key.
 */
export interface AccountBrand {
  palette?: BrandSwatch[];
  accent?: string;
  /** Second accent stop; absent = flat. Only meaningful beside `accent`. */
  accent2?: string;
  /** How the accent pair runs; absent = diagonal. Only meaningful beside `accent2`. */
  accentGradient?: GradientDir;
  bg?: string;
  font?: { body?: string; heading?: string };
  mode?: 'light' | 'dark' | 'auto';
  logos?: FormLogo[];
  updatedAt?: string;
}

/**
 * The theme a form actually renders — the client's copy of the server's
 * `resolveTheme`, and the ONE way the client resolves a theme.
 *
 * Kept in lockstep with `server/src/types/form.ts`, where the reasoning lives.
 * In short: per KEY not per blob; `logos: []` is an answer and beats the
 * account's row while an absent key inherits it; nothing is normalised on the
 * way through (`themeLogos` still does that, after); entitlement is never
 * consulted, because a lapsed plan must not strip a live form of its look.
 *
 * The public form never calls this — the server resolves before the theme
 * leaves, so a respondent is sent one shape and no palette they have no use
 * for. It is the BUILDER that needs it: the canvas renders an unsaved draft,
 * which the server has not seen.
 */
export function resolveTheme<T extends {
  accent?: string; accent2?: string; accentGradient?: GradientDir;
  bg?: string; mode?: 'light' | 'dark' | 'auto';
  font?: { body?: string; heading?: string };
  logos?: FormLogo[]; logo?: { url: string; height?: number; align?: LogoAlign };
}>(brand: AccountBrand | undefined, theme: T | undefined): T {
  const own = { ...((theme || {}) as T) };
  if (!brand) return own;

  // The accent PAIR is one answer: a form that picked its own accent picked a
  // flat one, and the house's second stop must not gradient it.
  if (own.accent === undefined && brand.accent !== undefined) {
    own.accent = brand.accent;
    if (own.accent2 === undefined && brand.accent2 !== undefined) own.accent2 = brand.accent2;
    if (own.accentGradient === undefined && brand.accentGradient !== undefined) {
      own.accentGradient = brand.accentGradient;
    }
  }
  if (own.bg === undefined && brand.bg !== undefined) own.bg = brand.bg;
  if (own.mode === undefined && brand.mode !== undefined) own.mode = brand.mode;

  const body = own.font?.body ?? brand.font?.body;
  const heading = own.font?.heading ?? brand.font?.heading;
  if (body !== undefined || heading !== undefined) {
    own.font = {
      ...(body !== undefined ? { body } : {}),
      ...(heading !== undefined ? { heading } : {}),
    };
  }

  const answersLogos = (theme && ('logos' in theme || 'logo' in theme)) || false;
  if (!answersLogos && brand.logos?.length) own.logos = brand.logos;

  return own;
}

/**
 * The accent band, as a `background` value — the ONE copy of the expression
 * `form.css` draws for `.of-page` and `.of-btn`.
 *
 * Those rules default to `linear-gradient(135deg, var(--of-accent) 0%,
 * var(--of-accent2) 100%)`, and the renderers set `--of-accent2` to the accent
 * itself when a theme names no second stop — so one colour is a flat band and
 * two are a gradient. Anything drawing the same band in an inline style (the
 * builder's canvas, the Branding preview) reads it from here instead of
 * restating the stops, which is how the canvas came to promise a purple no
 * respondent got.
 *
 * A theme that names a direction is why the renderers now hand the whole
 * value down as `--of-accent-wash`: an angle cannot be passed as two
 * variables into a template the stylesheet owns.
 */
export function accentWash(
  theme: { accent?: string; accent2?: string; accentGradient?: GradientDir } | undefined
): string {
  const a = theme?.accent || '#667eea';
  const b = theme?.accent2 || theme?.accent || '#764ba2';
  return gradientCss(a, b, theme?.accentGradient);
}
